/**
 * DemocratCharts — watchlist state.
 *
 * Users can have multiple named watchlists (e.g. "Indian Indices", "US Tech",
 * "Family Picks"). Each watchlist holds a set of (symbol, exchange) pairs.
 *
 * Storage: localStorage. Phase 5.3+ will sync to backend.
 */

import { create } from "zustand";
import { SYMBOLS, type SymbolEntry } from "@/lib/symbols";

export interface WatchlistSymbol {
  symbol: string;
  exchange: string;
  /** Optional display label override. Falls back to the symbol catalog. */
  name?: string;
}

export interface Watchlist {
  id: string;
  name: string;
  symbols: WatchlistSymbol[];
}

const LS_KEY = "livecharts:watchlists:v1";
const LS_ACTIVE_KEY = "livecharts:watchlists:active";

function defaultWatchlists(): Watchlist[] {
  // Seed with two starter lists drawn from our curated SYMBOLS catalog
  return [
    {
      id: "wl_indian",
      name: "Indian Markets",
      symbols: SYMBOLS
        .filter((s) => s.category === "Indian Index" || s.category === "Indian Equity")
        .slice(0, 8)
        .map((s) => ({ symbol: s.symbol, exchange: s.exchange, name: s.name })),
    },
    {
      id: "wl_us",
      name: "US Tech",
      symbols: SYMBOLS
        .filter((s) => s.category === "US Equity")
        .slice(0, 6)
        .map((s) => ({ symbol: s.symbol, exchange: s.exchange, name: s.name })),
    },
  ];
}

function loadWatchlists(): Watchlist[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Watchlist[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    /* ignore */
  }
  return defaultWatchlists();
}

function saveWatchlists(list: Watchlist[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

function loadActiveId(lists: Watchlist[]): string | null {
  try {
    const raw = localStorage.getItem(LS_ACTIVE_KEY);
    if (raw && lists.some((l) => l.id === raw)) return raw;
  } catch {
    /* ignore */
  }
  return lists[0]?.id ?? null;
}

function saveActiveId(id: string | null) {
  try {
    if (id) localStorage.setItem(LS_ACTIVE_KEY, id);
    else localStorage.removeItem(LS_ACTIVE_KEY);
  } catch {
    /* ignore */
  }
}

function makeId(): string {
  return `wl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

interface WatchlistsState {
  lists: Watchlist[];
  activeId: string | null;

  createList: (name: string) => string;
  renameList: (id: string, name: string) => void;
  deleteList: (id: string) => void;
  setActive: (id: string) => void;

  addSymbol: (listId: string, sym: WatchlistSymbol) => void;
  removeSymbol: (listId: string, symbol: string, exchange: string) => void;
  reorderSymbol: (listId: string, from: number, to: number) => void;
}

export const useWatchlistsStore = create<WatchlistsState>((set, get) => {
  const lists = loadWatchlists();
  return {
    lists,
    activeId: loadActiveId(lists),

    createList: (name) => {
      const id = makeId();
      const next = [...get().lists, { id, name: name.trim() || "New list", symbols: [] }];
      saveWatchlists(next);
      saveActiveId(id);
      set({ lists: next, activeId: id });
      return id;
    },

    renameList: (id, name) => {
      const next = get().lists.map((l) => (l.id === id ? { ...l, name } : l));
      saveWatchlists(next);
      set({ lists: next });
    },

    deleteList: (id) => {
      const next = get().lists.filter((l) => l.id !== id);
      saveWatchlists(next);
      const newActive = get().activeId === id ? (next[0]?.id ?? null) : get().activeId;
      saveActiveId(newActive);
      set({ lists: next, activeId: newActive });
    },

    setActive: (id) => {
      saveActiveId(id);
      set({ activeId: id });
    },

    addSymbol: (listId, sym) => {
      const next = get().lists.map((l) => {
        if (l.id !== listId) return l;
        // Dedupe by symbol+exchange
        if (l.symbols.some((s) => s.symbol === sym.symbol && s.exchange === sym.exchange)) return l;
        return { ...l, symbols: [...l.symbols, sym] };
      });
      saveWatchlists(next);
      set({ lists: next });
    },

    removeSymbol: (listId, symbol, exchange) => {
      const next = get().lists.map((l) =>
        l.id !== listId ? l : { ...l, symbols: l.symbols.filter((s) => !(s.symbol === symbol && s.exchange === exchange)) }
      );
      saveWatchlists(next);
      set({ lists: next });
    },

    reorderSymbol: (listId, from, to) => {
      const next = get().lists.map((l) => {
        if (l.id !== listId) return l;
        const arr = [...l.symbols];
        const [moved] = arr.splice(from, 1);
        arr.splice(to, 0, moved);
        return { ...l, symbols: arr };
      });
      saveWatchlists(next);
      set({ lists: next });
    },
  };
});
